import { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Field } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { PanelHeader } from "./PanelHeader";
import { EmptyState } from "../ui/empty-state";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Book } from "../../types";
import {
  CODEX_ENTRY_TYPES,
  CODEX_ENTRY_TYPE_LABELS,
  buildCodexContextSnippet,
  createBlankCodexEntry,
  getCodex,
  upsertCodexEntry,
  type CodexEntry,
  type CodexEntryType,
  type CodexState,
} from "../../../shared/codex";

type Props = {
  book: Book;
  onBookChange: (updates: Partial<Book>) => void;
  onInjectContext?: (snippet: string) => void;
  /** Optional chapter text to bias context snippet matching. */
  chapterText?: string;
  /**
   * Merge characters & locations rosters into Codex.
   * Parent should confirm externally or return counts after applying.
   */
  onSyncFromRosters?: () => { added: number; updated: number };
};

function persistCodex(state: CodexState, onBookChange: (updates: Partial<Book>) => void) {
  onBookChange({ metadata: { codex: state } });
}

export function CodexPanel({
  book,
  onBookChange,
  onInjectContext,
  chapterText,
  onSyncFromRosters,
}: Props) {
  const state = useMemo(() => getCodex(book), [book]);
  const [filter, setFilter] = useState<CodexEntryType | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const list = filter === "all" ? state.entries : state.entries.filter((e) => e.type === filter);
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [state.entries, filter]);

  const selected: CodexEntry | null =
    filtered.find((e) => e.id === selectedId) ??
    state.entries.find((e) => e.id === selectedId) ??
    null;

  function patchSelected(patch: Partial<CodexEntry>) {
    if (!selected) return;
    const next = upsertCodexEntry(state, { ...selected, ...patch });
    persistCodex(next, onBookChange);
  }

  function addEntry() {
    const entry = createBlankCodexEntry(filter === "all" ? "custom" : filter);
    const next = upsertCodexEntry(state, entry);
    persistCodex(next, onBookChange);
    setSelectedId(entry.id);
  }

  function handleCopyContext() {
    const snippet = buildCodexContextSnippet(book, {
      chapterText,
      limit: 12,
    });
    if (onInjectContext) {
      onInjectContext(snippet);
      setCopyNotice("Context sent to chat");
    } else if (snippet && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(snippet).then(() => {
        setCopyNotice("Copied to clipboard");
      });
    } else {
      setCopyNotice(snippet ? "Nothing to inject" : "Codex is empty");
    }
    window.setTimeout(() => setCopyNotice(null), 2500);
  }

  function handleSyncFromRosters() {
    if (!onSyncFromRosters) return;
    const confirmed = window.confirm(
      "Merge characters and locations into Codex?\n\n" +
        "Existing matching entries will be updated (name, aliases, summary). " +
        "Lore body, links, and custom entries are preserved. Continue?"
    );
    if (!confirmed) return;
    const { added, updated } = onSyncFromRosters();
    const merged = added + updated;
    setSyncStatus(
      merged > 0
        ? `Merged ${merged} entr${merged === 1 ? "y" : "ies"} (${added} added, ${updated} updated)`
        : "No roster entries to merge"
    );
    window.setTimeout(() => setSyncStatus(null), 4000);
  }

  return (
    <div className="m-1 flex h-full min-h-0 flex-1 flex-col overflow-auto rounded-lg border border-border bg-card px-[0.85rem] pt-3 pb-4 shadow-sm">
      <PanelHeader
        title="Codex"
        description="Characters, places, lore, and items — shared world bible for chat and drafting."
        className="mb-3"
        actions={
          <>
            <Button type="button" size="sm" variant="secondary" onClick={addEntry}>
              Add entry
            </Button>
            {onSyncFromRosters ? (
              <Button type="button" size="sm" variant="secondary" onClick={handleSyncFromRosters}>
                Sync from characters &amp; locations
              </Button>
            ) : null}
            <Button type="button" size="sm" onClick={handleCopyContext}>
              Copy context for chat
            </Button>
          </>
        }
      />
      {copyNotice ? (
        <p className="text-xs text-muted-foreground" role="status">
          {copyNotice}
        </p>
      ) : null}
      {syncStatus ? (
        <p className="text-xs text-muted-foreground" role="status">
          {syncStatus}
        </p>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-pressed={filter === "all"}
          className={cn(
            "h-auto min-w-0 flex-1 basis-22 px-1 py-2 leading-[1.4]",
            filter === "all" && "border-primary bg-primary/10 text-primary hover:bg-primary/15"
          )}
          onClick={() => setFilter("all")}
        >
          All
        </Button>
        {CODEX_ENTRY_TYPES.map((t) => (
          <Button
            key={t}
            type="button"
            size="sm"
            variant="outline"
            aria-pressed={filter === t}
            className={cn(
              "h-auto min-w-0 flex-1 basis-22 px-1 py-2 leading-[1.4]",
              filter === t && "border-primary bg-primary/10 text-primary hover:bg-primary/15"
            )}
            onClick={() => setFilter(t)}
          >
            {CODEX_ENTRY_TYPE_LABELS[t]}
          </Button>
        ))}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(10rem,13.75rem)_minmax(0,1fr)] gap-4 max-[720px]:grid-cols-1">
        <ul className="m-0 list-none overflow-auto rounded-md border border-border/60 bg-muted/10 p-0 max-[720px]:max-h-48">
          {filtered.length === 0 ? (
            <li>
              <EmptyState compact title="No entries yet." />
            </li>
          ) : (
            filtered.map((e) => (
              <li key={e.id} className="border-b border-border/50 last:border-b-0">
                <Button
                  type="button"
                  variant="ghost"
                  aria-pressed={selected?.id === e.id}
                  className={cn(
                    "h-auto w-full justify-start rounded-none px-2.5 py-2 text-left",
                    selected?.id === e.id && "bg-primary/10 font-semibold hover:bg-primary/15"
                  )}
                  onClick={() => setSelectedId(e.id)}
                >
                  <span className="block min-w-0">
                    <span className="block text-xs text-muted-foreground">
                      {CODEX_ENTRY_TYPE_LABELS[e.type]}
                    </span>
                    <span className="block truncate">{e.name}</span>
                  </span>
                </Button>
              </li>
            ))
          )}
        </ul>

        <div className="min-w-0 overflow-auto px-0.5 pt-0.5 pb-2">
          {!selected ? (
            <p className="text-xs text-muted-foreground">Select an entry to edit.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              <Field label="Type">
                <Select
                  value={selected.type}
                  onValueChange={(value) =>
                    patchSelected({ type: value as CodexEntryType })
                  }
                >
                  <SelectTrigger className="w-full" size="default" aria-label="Codex entry type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CODEX_ENTRY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {CODEX_ENTRY_TYPE_LABELS[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Name">
                <Input
                  value={selected.name}
                  onChange={(ev) => patchSelected({ name: ev.target.value })}
                />
              </Field>
              <Field label="Aliases (comma-separated)">
                <Input
                  value={selected.aliases.join(", ")}
                  onChange={(ev) =>
                    patchSelected({
                      aliases: ev.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </Field>
              <Field label="Summary">
                <Textarea
                  value={selected.summary}
                  rows={2}
                  onChange={(ev) => patchSelected({ summary: ev.target.value })}
                />
              </Field>
              <Field label="Body">
                <Textarea
                  value={selected.body}
                  rows={8}
                  onChange={(ev) => patchSelected({ body: ev.target.value })}
                />
              </Field>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
